import React from "react";
import { Box, Typography, TextField, FormControl, Select, MenuItem } from "@material-ui/core";
import AICaptureBadge from "../BusinessProfileCard/AICaptureBadge";
import { D11, CONTACT360_SELECT_MENU_Z } from "./contact360.constants";
import { getAiConfidence, stringifyProfileEditableValue, toDateInputValue } from "./contact360.helpers";

// D8.4.3 — Campos presentacionales del perfil (texto / fecha / select con hint de IA).

export function ProfileField({
  label,
  fieldKey,
  value,
  onChange,
  aiExtractedData,
  multiline,
  rows,
  disabled
}) {
  const confidence = getAiConfidence(aiExtractedData, fieldKey);
  const isAiHint = confidence != null && confidence >= 0.6;
  const display = stringifyProfileEditableValue(value);
  const empty = display === "";

  const inputSx = {};
  if (empty) {
    Object.assign(inputSx, { fontStyle: "italic", color: D11.placeholder });
  }

  let rootSx = {};
  if (isAiHint) {
    rootSx.borderRadius = 8;
    rootSx.borderLeft = `3px solid ${D11.green}`;
  }

  const mergedRootStyle =
    empty || Object.keys(rootSx).length
      ? { ...(empty ? inputSx : {}), ...rootSx }
      : undefined;

  return (
    <Box display="flex" alignItems="flex-start" mb={2} flexWrap="wrap">
      <Typography variant="body2" style={{ minWidth: 160, paddingTop: 8, marginRight: 8 }}>
        {label}
      </Typography>
        <Box flex={1} minWidth={220} display="flex" alignItems="flex-start" flexWrap="wrap">
          <TextField
            disabled={disabled}
            fullWidth
            size="small"
            variant="outlined"
            multiline={multiline}
            rows={multiline ? rows || 4 : undefined}
            value={display}
            placeholder="Sin registrar"
            onChange={(e) => onChange(fieldKey, e.target.value)}
            InputProps={{
              style: mergedRootStyle
            }}
          />
          <Box style={{ marginLeft: 8, marginTop: 4 }}>
            <AICaptureBadge confidence={confidence} />
          </Box>
        </Box>
    </Box>
  );
}

export function ProfileDateField({ label, fieldKey, value, onChange, aiExtractedData, disabled }) {
  const confidence = getAiConfidence(aiExtractedData, fieldKey);
  const isAiHint = confidence != null && confidence >= 0.6;
  const display = toDateInputValue(value);
  const empty = display === "";

  const inputSx = {};
  if (empty) {
    Object.assign(inputSx, { fontStyle: "italic", color: D11.placeholder });
  }
  let rootSx = {};
  if (isAiHint) {
    rootSx.borderRadius = 8;
    rootSx.borderLeft = `3px solid ${D11.green}`;
  }
  const mergedRootStyle =
    empty || Object.keys(rootSx).length
      ? { ...(empty ? inputSx : {}), ...rootSx }
      : undefined;

  return (
    <Box display="flex" alignItems="flex-start" mb={2} flexWrap="wrap">
      <Typography variant="body2" style={{ minWidth: 160, paddingTop: 8, marginRight: 8 }}>
        {label}
      </Typography>
      <Box flex={1} minWidth={220} display="flex" alignItems="flex-start" flexWrap="wrap">
        <TextField
          disabled={disabled}
          fullWidth
          size="small"
          variant="outlined"
          type="date"
          value={display}
          placeholder="Sin registrar"
          onChange={(e) => onChange(fieldKey, e.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            style: mergedRootStyle
          }}
        />
        <Box style={{ marginLeft: 8, marginTop: 4 }}>
          <AICaptureBadge confidence={confidence} />
        </Box>
      </Box>
    </Box>
  );
}

export function ProfileAiSelect({
  label,
  fieldKey,
  value,
  onChange,
  options,
  aiExtractedData,
  disabled,
  allowEmpty,
  emptyLabel = "Seleccionar…",
  extraLegacyOption
}) {
  const confidence = getAiConfidence(aiExtractedData, fieldKey);
  const isAiHint = confidence != null && confidence >= 0.6;
  const normalized = value == null || value === "" ? "" : String(value);

  const safeOptions = Array.isArray(options) ? options : [];

  let rootSx = {};
  if (isAiHint) {
    rootSx.borderRadius = 8;
    rootSx.borderLeft = `3px solid ${D11.green}`;
  }

  const optionMap = safeOptions.reduce((acc, o) => {
    acc[String(o.value)] = o.label;
    return acc;
  }, {});

  const renderSelected = () => {
    if (normalized === "") {
      return <span style={{ color: D11.placeholder, fontStyle: "italic" }}>{emptyLabel}</span>;
    }
    if (optionMap[normalized]) return optionMap[normalized];
    if (extraLegacyOption) return `Texto guardado: ${extraLegacyOption}`;
    return normalized;
  };

  return (
    <Box display="flex" alignItems="flex-start" mb={2} flexWrap="wrap">
      <Typography variant="body2" style={{ minWidth: 160, paddingTop: 8, marginRight: 8 }}>
        {label}
      </Typography>
      <Box flex={1} minWidth={220} display="flex" alignItems="flex-start" flexWrap="wrap">
        <FormControl
          fullWidth
          size="small"
          variant="outlined"
          disabled={disabled}
          style={{ ...rootSx }}
        >
          <Select
            displayEmpty
            value={normalized}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            renderValue={() => renderSelected()}
            MenuProps={{
              disableScrollLock: true,
              getContentAnchorEl: null,
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              /* No usar ModalProps aquí: Select v4 lo filtra mal y React lo pinta sobre un DOM <div>. */
              style: { zIndex: CONTACT360_SELECT_MENU_Z },
              PaperProps: {
                style: { maxHeight: 360, zIndex: CONTACT360_SELECT_MENU_Z }
              }
            }}
          >
            {allowEmpty !== false ? (
              <MenuItem value="">
                <em>{emptyLabel}</em>
              </MenuItem>
            ) : null}
            {extraLegacyOption && normalized && !safeOptions.some((o) => String(o.value) === normalized) ? (
              <MenuItem value={normalized}>Texto guardado: {extraLegacyOption}</MenuItem>
            ) : null}
            {safeOptions.map((o) => (
              <MenuItem key={String(o.value)} value={String(o.value)}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box style={{ marginLeft: 8, marginTop: 4 }}>
          <AICaptureBadge confidence={confidence} />
        </Box>
      </Box>
    </Box>
  );
}
